/**
 * Color Tokens
 *
 * Color palette system for Bimanova with curated theme presets
 * Based on sample design images - clean, minimalistic, professional
 *
 * Each preset includes:
 * - Primary color (main brand color)
 * - Secondary color (complementary accent)
 * - Tertiary color (additional accent)
 * - Full shade scale (50-900) for each
 * - Neutral grays for backgrounds/text
 * - Semantic colors (success, warning, error, info)
 */

// ============================================================================
// COLOR PRESET TYPE
// ============================================================================

export interface ColorScale {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string; // Base color
  600: string;
  700: string;
  800: string;
  900: string;
}

export interface SemanticColors {
  success: ColorScale;
  warning: ColorScale;
  error: ColorScale;
  info: ColorScale;
}

export interface ColorPreset {
  id: string;
  name: string;
  description: string;
  primary: ColorScale;
  secondary: ColorScale;
  tertiary: ColorScale;
  neutral: ColorScale;
  semantic: SemanticColors;
}

// ============================================================================
// NEUTRAL GRAYS (Shared across all presets)
// ============================================================================

export const NEUTRAL_GRAY: ColorScale = {
  50: '#FAFAFA',   // Almost white
  100: '#F5F5F5',  // Very light gray
  200: '#EEEEEE',  // Light gray
  300: '#E0E0E0',  // Medium light gray
  400: '#BDBDBD',  // Medium gray
  500: '#9E9E9E',  // Base gray
  600: '#757575',  // Medium dark gray
  700: '#616161',  // Dark gray
  800: '#424242',  // Very dark gray
  900: '#212121',  // Almost black
};

// ============================================================================
// SEMANTIC COLORS (Shared across all presets)
// ============================================================================

const SUCCESS_GREEN: ColorScale = {
  50: '#E8F5E9',
  100: '#C8E6C9',
  200: '#A5D6A7',
  300: '#81C784',
  400: '#66BB6A',
  500: '#4CAF50',   // Base green
  600: '#43A047',
  700: '#388E3C',
  800: '#2E7D32',
  900: '#1B5E20',
};

const WARNING_AMBER: ColorScale = {
  50: '#FFF8E1',
  100: '#FFECB3',
  200: '#FFE082',
  300: '#FFD54F',
  400: '#FFCA28',
  500: '#FFC107',   // Base amber
  600: '#FFB300',
  700: '#FFA000',
  800: '#FF8F00',
  900: '#FF6F00',
};

const ERROR_RED: ColorScale = {
  50: '#FFEBEE',
  100: '#FFCDD2',
  200: '#EF9A9A',
  300: '#E57373',
  400: '#EF5350',
  500: '#F44336',   // Base red
  600: '#E53935',
  700: '#D32F2F',
  800: '#C62828',
  900: '#B71C1C',
};

const INFO_BLUE: ColorScale = {
  50: '#E3F2FD',
  100: '#BBDEFB',
  200: '#90CAF9',
  300: '#64B5F6',
  400: '#42A5F5',
  500: '#2196F3',   // Base blue
  600: '#1E88E5',
  700: '#1976D2',
  800: '#1565C0',
  900: '#0D47A1',
};

const SEMANTIC_COLORS: SemanticColors = {
  success: SUCCESS_GREEN,
  warning: WARNING_AMBER,
  error: ERROR_RED,
  info: INFO_BLUE,
};

// ============================================================================
// PRESET 1: CORAL SUNRISE (Default - Based on Marketing & Sales sample)
// ============================================================================

const DEEP_ORANGE_PRIMARY: ColorScale = {
  50: '#FBE9E7',
  100: '#FFCCBC',
  200: '#FFAB91',
  300: '#FF8A65',
  400: '#FF7043',
  500: '#FF5722',   // Base deep orange (primary from marketing sample)
  600: '#F4511E',
  700: '#E64A19',
  800: '#D84315',
  900: '#BF360C',
};

const AMBER_SECONDARY: ColorScale = {
  50: '#FFF8E1',
  100: '#FFECB3',
  200: '#FFE082',
  300: '#FFD54F',
  400: '#FFCA28',
  500: '#FFC107',   // Base amber
  600: '#FFB300',
  700: '#FFA000',
  800: '#FF8F00',
  900: '#FF6F00',
};

const ORANGE_TERTIARY: ColorScale = {
  50: '#FFF3E0',
  100: '#FFE0B2',
  200: '#FFCC80',
  300: '#FFB74D',
  400: '#FFA726',
  500: '#FF9800',   // Base orange
  600: '#FB8C00',
  700: '#F57C00',
  800: '#EF6C00',
  900: '#E65100',
};

export const CORAL_SUNRISE: ColorPreset = {
  id: 'coral-sunrise',
  name: 'Coral Sunrise',
  description: 'Energetic and dynamic - Ideal for sales and marketing platforms',
  primary: DEEP_ORANGE_PRIMARY,
  secondary: AMBER_SECONDARY,
  tertiary: ORANGE_TERTIARY,
  neutral: NEUTRAL_GRAY,
  semantic: SEMANTIC_COLORS,
};

// ============================================================================
// PRESET 2: INDIGO WAVE (Based on Finance & Banking sample)
// ============================================================================

const INDIGO_PRIMARY: ColorScale = {
  50: '#E8EAF6',
  100: '#C5CAE9',
  200: '#9FA8DA',
  300: '#7986CB',
  400: '#5C6BC0',
  500: '#3F51B5',   // Base indigo (primary button color from samples)
  600: '#3949AB',
  700: '#303F9F',
  800: '#283593',
  900: '#1A237E',
};

const CYAN_SECONDARY: ColorScale = {
  50: '#E0F7FA',
  100: '#B2EBF2',
  200: '#80DEEA',
  300: '#4DD0E1',
  400: '#26C6DA',
  500: '#00BCD4',   // Base cyan
  600: '#00ACC1',
  700: '#0097A7',
  800: '#00838F',
  900: '#006064',
};

const LIGHT_BLUE_TERTIARY: ColorScale = {
  50: '#E1F5FE',
  100: '#B3E5FC',
  200: '#81D4FA',
  300: '#4FC3F7',
  400: '#29B6F6',
  500: '#03A9F4',   // Base light blue
  600: '#039BE5',
  700: '#0288D1',
  800: '#0277BD',
  900: '#01579B',
};

export const INDIGO_WAVE: ColorPreset = {
  id: 'indigo-wave',
  name: 'Indigo Wave',
  description: 'Professional and trustworthy - Perfect for financial and insurance applications',
  primary: INDIGO_PRIMARY,
  secondary: CYAN_SECONDARY,
  tertiary: LIGHT_BLUE_TERTIARY,
  neutral: NEUTRAL_GRAY,
  semantic: SEMANTIC_COLORS,
};

// ============================================================================
// PRESET 3: EMERALD FOREST (Based on AI Product sample - green icon)
// ============================================================================

const GREEN_PRIMARY: ColorScale = {
  50: '#E8F5E9',
  100: '#C8E6C9',
  200: '#A5D6A7',
  300: '#81C784',
  400: '#66BB6A',
  500: '#4CAF50',   // Base green (from AI product icon)
  600: '#43A047',
  700: '#388E3C',
  800: '#2E7D32',
  900: '#1B5E20',
};

const TEAL_SECONDARY: ColorScale = {
  50: '#E0F2F1',
  100: '#B2DFDB',
  200: '#80CBC4',
  300: '#4DB6AC',
  400: '#26A69A',
  500: '#009688',   // Base teal
  600: '#00897B',
  700: '#00796B',
  800: '#00695C',
  900: '#004D40',
};

const LIME_TERTIARY: ColorScale = {
  50: '#F9FBE7',
  100: '#F0F4C3',
  200: '#E6EE9C',
  300: '#DCE775',
  400: '#D4E157',
  500: '#CDDC39',   // Base lime
  600: '#C0CA33',
  700: '#AFB42B',
  800: '#9E9D24',
  900: '#827717',
};

export const EMERALD_FOREST: ColorPreset = {
  id: 'emerald-forest',
  name: 'Emerald Forest',
  description: 'Fresh and growth-oriented - Great for analytics and performance tracking',
  primary: GREEN_PRIMARY,
  secondary: TEAL_SECONDARY,
  tertiary: LIME_TERTIARY,
  neutral: NEUTRAL_GRAY,
  semantic: SEMANTIC_COLORS,
};

// ============================================================================
// PRESET 4: RUBY PASSION (Based on Cryptocurrency sample - red icon)
// ============================================================================

const RED_PRIMARY: ColorScale = {
  50: '#FFEBEE',
  100: '#FFCDD2',
  200: '#EF9A9A',
  300: '#E57373',
  400: '#EF5350',
  500: '#F44336',   // Base red (from crypto app icon)
  600: '#E53935',
  700: '#D32F2F',
  800: '#C62828',
  900: '#B71C1C',
};

const PINK_SECONDARY: ColorScale = {
  50: '#FCE4EC',
  100: '#F8BBD0',
  200: '#F48FB1',
  300: '#F06292',
  400: '#EC407A',
  500: '#E91E63',   // Base pink
  600: '#D81B60',
  700: '#C2185B',
  800: '#AD1457',
  900: '#880E4F',
};

const DEEP_PURPLE_TERTIARY: ColorScale = {
  50: '#EDE7F6',
  100: '#D1C4E9',
  200: '#B39DDB',
  300: '#9575CD',
  400: '#7E57C2',
  500: '#673AB7',   // Base deep purple
  600: '#5E35B1',
  700: '#512DA8',
  800: '#4527A0',
  900: '#311B92',
};

export const RUBY_PASSION: ColorPreset = {
  id: 'ruby-passion',
  name: 'Ruby Passion',
  description: 'Bold and attention-grabbing - Perfect for high-priority actions and alerts',
  primary: RED_PRIMARY,
  secondary: PINK_SECONDARY,
  tertiary: DEEP_PURPLE_TERTIARY,
  neutral: NEUTRAL_GRAY,
  semantic: SEMANTIC_COLORS,
};

// ============================================================================
// PRESET 5: SLATE PROFESSIONAL (Neutral professional theme)
// ============================================================================

const BLUE_GRAY_PRIMARY: ColorScale = {
  50: '#ECEFF1',
  100: '#CFD8DC',
  200: '#B0BEC5',
  300: '#90A4AE',
  400: '#78909C',
  500: '#607D8B',   // Base blue gray
  600: '#546E7A',
  700: '#455A64',
  800: '#37474F',
  900: '#263238',
};

const SLATE_SECONDARY: ColorScale = {
  50: '#ECEFF1',
  100: '#CFD8DC',
  200: '#B0BEC5',
  300: '#90A4AE',
  400: '#78909C',
  500: '#607D8B',   // Base slate
  600: '#546E7A',
  700: '#455A64',
  800: '#37474F',
  900: '#263238',
};

const GRAY_TERTIARY: ColorScale = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#EEEEEE',
  300: '#E0E0E0',
  400: '#BDBDBD',
  500: '#9E9E9E',   // Base gray
  600: '#757575',
  700: '#616161',
  800: '#424242',
  900: '#212121',
};

export const SLATE_PROFESSIONAL: ColorPreset = {
  id: 'slate-professional',
  name: 'Slate Professional',
  description: 'Refined and understated - Ideal for corporate and enterprise environments',
  primary: BLUE_GRAY_PRIMARY,
  secondary: SLATE_SECONDARY,
  tertiary: GRAY_TERTIARY,
  neutral: NEUTRAL_GRAY,
  semantic: SEMANTIC_COLORS,
};

// ============================================================================
// ALL PRESETS COLLECTION
// ============================================================================

export const COLOR_PRESETS: ColorPreset[] = [
  CORAL_SUNRISE,      // Default (as requested)
  INDIGO_WAVE,
  EMERALD_FOREST,
  RUBY_PASSION,
  SLATE_PROFESSIONAL,
];

export const DEFAULT_PRESET = CORAL_SUNRISE;

// ============================================================================
// BRAND COLORS (Placeholder for future brand colors)
// ============================================================================

/**
 * Brand colors placeholder
 * Replace these values when brand guidelines are established
 */
export const BRAND_COLORS = {
  /**
   * To add brand colors later:
   * 1. Define your brand color scales here
   * 2. Create a new ColorPreset using these scales
   * 3. Add it to COLOR_PRESETS array
   * 4. Set it as DEFAULT_PRESET if needed
   *
   * Example:
   * export const BIMANOVA_BRAND: ColorPreset = {
   *   id: 'bimanova-brand',
   *   name: 'Bimanova Brand',
   *   description: 'Official Bimanova brand colors',
   *   primary: YOUR_BRAND_PRIMARY_SCALE,
   *   secondary: YOUR_BRAND_SECONDARY_SCALE,
   *   tertiary: YOUR_BRAND_TERTIARY_SCALE,
   *   neutral: NEUTRAL_GRAY,
   *   semantic: SEMANTIC_COLORS,
   * };
   */
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get preset by ID
 */
export const getPresetById = (id: string): ColorPreset | undefined => {
  return COLOR_PRESETS.find((preset) => preset.id === id);
};

/**
 * Get preset by name
 */
export const getPresetByName = (name: string): ColorPreset | undefined => {
  return COLOR_PRESETS.find((preset) => preset.name === name);
};

/**
 * Get all preset IDs
 */
export const getPresetIds = (): string[] => {
  return COLOR_PRESETS.map((preset) => preset.id);
};

/**
 * Get all preset names
 */
export const getPresetNames = (): string[] => {
  return COLOR_PRESETS.map((preset) => preset.name);
};
